const prefix = 'mow';

const isAvailable = (): boolean => {
  try {
    const testKey = "__storage_test__";
    window.localStorage.setItem(testKey, "1");
    window.localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    console.warn("LocalStorage is not available:", e);
    return false;
  }
};

const buildKey = (key: string): string => `${prefix}:${key}`;

export const storeItem = <T>(key: string, value: T): void => {
  if (!isAvailable()) return;
  try {
    const json = JSON.stringify(value);
    window.localStorage.setItem(buildKey(key), json);
  } catch (e) {
    console.error(`Failed to store item "${key}"`, e);
  }
};

export const retrieveItem = <T>(key: string): T | null => {
  if (!isAvailable()) return null;
  try {
    const value = window.localStorage.getItem(buildKey(key));
    return value ? (JSON.parse(value) as T) : null;
  } catch (e) {
    console.error(`Failed to parse item "${key}"`, e);
    return null;
  }
};

export const removeItem = (key: string): void => {
  if (!isAvailable()) return;
  try {
    window.localStorage.removeItem(buildKey(key));
  } catch (e) {
    console.error(`Failed to remove item "${key}"`, e);
  }
};

export const clear = (): void => {
  if (!isAvailable()) return;
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith(`${prefix}:`))
      .forEach((k) => window.localStorage.removeItem(k));
  } catch (e) {
    console.error("Failed to clear namespace", e);
  }
};

export const getAll = <T = unknown>(): Record<string, T> => {
  if (!isAvailable()) return {};
  const data: Record<string, T> = {};
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith(`${prefix}:`))
      .forEach((k) => {
        const raw = window.localStorage.getItem(k);
        if (raw) {
          try {
            data[k.replace(`${prefix}:`, "")] = JSON.parse(raw);
          } catch {
            // Skip invalid JSON
          }
        }
      });
  } catch (e) {
    console.error("Failed to get all items", e);
  }
  return data;
};
